import React, { useEffect } from 'react';
import { useStore } from '../../stores';
import { VolumeService } from '../../services/volume/VolumeService';
import { BrightnessService } from '../../services/brightness/BrightnessService';
import { usePlatform } from '../../services/platform';
import { isDesktopBrowser } from '../../utils/isDesktopBrowser';
import { isHostVolumeControlled } from '../../services/embedded';
import { resolveApiBaseUrl } from '../../services/embedded/hostConfig';
import { CONFIG_KEY } from '../../services/config/playerConfigStorage';

interface PlayerConfig {
  volumeLevel: number;
  resetVolumeTimeoutSeconds: number;

  batteryWarningEnabled?: boolean;
  audioAlertEnabled?: boolean;
  audioAlertVolumeLevel?: number;
  audioAlertBeepCount?: number;
  audioAlertIntervalMinutes?: number;
  audioAlertLowBatteryBeepCount?: number;
  audioAlertLowBatteryIntervalMinutes?: number;
  visualAlertEnabled?: boolean;
  visualAlertBlinkCount?: number;
  visualAlertDurationSeconds?: number;
  visualAlertLowBatteryBlinkCount?: number;
  visualAlertLowBatteryDurationSeconds?: number;

  backgroundPortraitUrl?: string;
  backgroundLandscapeUrl?: string;

  // V046 — Brilho do display
  brightnessAuto?: boolean;
  brightnessLevel?: number;

  // V058 — Manter tela sempre ativa (browser)
  keepScreenAwake?: boolean;

  updatedAt?: string;
}

/**
 * V046 — aplica brilho com base no config. brightnessAuto=true (ou
 * undefined) deixa o SO controlar (remove overlay). false aplica
 * brightnessLevel manual. Idempotente — pode ser chamado em qualquer
 * apply point sem efeitos colaterais.
 *
 * 2026-05-29 — aceita TANTO camelCase quanto snake_case. Backend Java tem
 * `spring.jackson.property-naming-strategy=SNAKE_CASE` global, então via
 * SSE/HTTP os campos chegam como `brightness_auto`/`brightness_level`. Sem
 * o cast, `config.brightnessAuto` ficava sempre `undefined` → cai em
 * `setAuto()` mesmo quando admin configurou `auto=false` + nível
 * específico. Bug observado: tablet/TV reportando "Brilho: N/A" ou "0%"
 * mesmo após config no admin.
 */
export function applyBrightness(
  brightnessService: BrightnessService,
  config: { brightnessAuto?: boolean; brightnessLevel?: number },
): void {
  // Aceita snake_case (vindo do backend) E camelCase (vindo do localStorage
  // já normalizado). camelCase tem prioridade quando ambos existem.
  const raw = config as unknown as Record<string, unknown>;
  const auto =
    typeof config.brightnessAuto === 'boolean'
      ? config.brightnessAuto
      : typeof raw.brightness_auto === 'boolean'
        ? (raw.brightness_auto as boolean)
        : undefined;
  const level =
    typeof config.brightnessLevel === 'number'
      ? config.brightnessLevel
      : typeof raw.brightness_level === 'number'
        ? (raw.brightness_level as number)
        : undefined;

  if (auto !== false) {
    brightnessService.setAuto();
    return;
  }
  brightnessService.setBrightness(level ?? 50);
}

/**
 * V058 — Aplica `keepScreenAwake`. No browser faz request/release do Screen
 * Wake Lock (mantém a tela acesa); em Android Device Owner / Tizen / WebOS é
 * no-op (tela acesa nativamente). Aceita snake_case (backend SNAKE_CASE) e
 * camelCase. Default `true` — signage quer a tela sempre acesa.
 */
export function applyKeepScreenAwake(
  platform: {
    requestScreenWakeLock: () => Promise<void>;
    releaseScreenWakeLock: () => Promise<void>;
  },
  config: { keepScreenAwake?: boolean },
): void {
  const raw = config as unknown as Record<string, unknown>;
  const keep =
    typeof config.keepScreenAwake === 'boolean'
      ? config.keepScreenAwake
      : typeof raw.keep_screen_awake === 'boolean'
        ? (raw.keep_screen_awake as boolean)
        : true; // default ON
  if (keep) {
    void platform.requestScreenWakeLock();
  } else {
    void platform.releaseScreenWakeLock();
  }
}

export const ConfigManager: React.FC = () => {
  const { syncStore, authStore } = useStore();
  const platform = usePlatform();
  const volumeService = VolumeService.getInstance();
  const brightnessService = BrightnessService.getInstance();

  // V047 — habilita caminho de hardware no BrightnessService. Idempotente.
  useEffect(() => {
    brightnessService.init(platform);
  }, [platform, brightnessService]);

  // Cache a background image URL as a data URL in localStorage for offline use
  const cacheBackgroundImage = async (
    url: string | undefined,
    storageKey: string,
  ) => {
    if (!url) {
      // If URL removed, clear the cache
      localStorage.removeItem(storageKey);
      return;
    }

    // Check if already cached with same URL
    const cachedMeta = localStorage.getItem(storageKey + '_url');
    if (cachedMeta === url) {
      console.log(`[ConfigManager] Background ${storageKey} already cached`);
      return;
    }

    try {
      console.log(`[ConfigManager] Caching background image: ${url}`);
      const response = await fetch(url);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        try {
          localStorage.setItem(storageKey, dataUrl);
          localStorage.setItem(storageKey + '_url', url);
          console.log(
            `[ConfigManager] Cached background ${storageKey} (${(dataUrl.length / 1024).toFixed(0)}KB)`,
          );
        } catch (e) {
          console.warn(
            `[ConfigManager] Failed to cache ${storageKey} (may be too large for localStorage)`,
            e,
          );
        }
      };
      reader.readAsDataURL(blob);
    } catch (e) {
      console.warn(
        `[ConfigManager] Failed to download background ${storageKey}:`,
        e,
      );
    }
  };

  // FONTE ÚNICA (utils/isDesktopBrowser) — exclui Smart TV (Tizen/webOS).
  // Cópia local antiga classificava a Tizen como desktop (UA tem "LINUX") e
  // setava disableVolume=true → o volume da config NUNCA era aplicado na TV
  // (setVolume + watchdog pulados). Bug diagnosticado 2026-08-03 via logs.
  const disableVolume = isDesktopBrowser();

  useEffect(() => {
    // Load persisted config and apply on mount
    const loadConfig = async () => {
      // Helper to resolve relative backend URLs to full URLs
      const contentApiUrl = resolveApiBaseUrl();
      const resolveUrl = (url?: string): string | undefined => {
        if (!url) return undefined;
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        return `${contentApiUrl}${url.startsWith('/') ? '' : '/'}${url}`;
      };

      // 1. Load local config first for immediate feedback
      const storedConfig = localStorage.getItem(CONFIG_KEY);
      if (storedConfig) {
        try {
          const config: PlayerConfig = JSON.parse(storedConfig);
          console.log('[ConfigManager] Loading stored config:', config);
          // Host-owned volume (embedded): se o host assumiu o volume, o servidor
          // NÃO reaplica. Fora do embedded isHostVolumeControlled() é sempre
          // false → comportamento idêntico ao de antes (frota intacta).
          if (
            !disableVolume &&
            config.volumeLevel !== undefined &&
            !isHostVolumeControlled()
          ) {
            await volumeService.setVolume(config.volumeLevel);
          }
          applyBrightness(brightnessService, config);
          // V058 — manter tela ativa (browser); no-op nas demais plataformas
          applyKeepScreenAwake(platform, config);

          // Cache backgrounds from stored config immediately (no auth needed)
          const storedPortrait = resolveUrl(config.backgroundPortraitUrl);
          const storedLandscape = resolveUrl(config.backgroundLandscapeUrl);
          if (storedPortrait || storedLandscape) {
            console.log(
              '[ConfigManager] Caching backgrounds from stored config:',
              { storedPortrait, storedLandscape },
            );
            cacheBackgroundImage(storedPortrait, 'efflux_bg_portrait');
            cacheBackgroundImage(storedLandscape, 'efflux_bg_landscape');
          }
        } catch (e) {
          console.error('[ConfigManager] Error loading config:', e);
        }
      }

      // 2. Fetch latest config from server
      // Use device ID check instead of authStore.isAuthenticated
      // because SyncService authenticates via X-Device-Id header independently
      const deviceId =
        authStore.deviceId || localStorage.getItem('efflux_device_id');

      const doRemoteFetch = async () => {
        if (!syncStore.isInitialized) {
          console.log(
            '[ConfigManager] SyncStore not ready, deferring config fetch...',
          );
          // Retry after 5s to give SyncStore time to initialize
          setTimeout(doRemoteFetch, 5000);
          return;
        }

        try {
          console.log('[ConfigManager] Fetching latest config from server...');
          const remoteConfig = await syncStore.fetchConfig();

          if (remoteConfig) {
            console.log(
              '[ConfigManager] Received remote config:',
              remoteConfig,
            );

            // Merge with existing (or default)
            const currentStored = localStorage.getItem(CONFIG_KEY);
            let baseConfig: PlayerConfig = {
              volumeLevel: 50,
              resetVolumeTimeoutSeconds: 0,
              batteryWarningEnabled: true,
              audioAlertEnabled: true,
              audioAlertVolumeLevel: 80,
              audioAlertBeepCount: 3,
              audioAlertIntervalMinutes: 5,
              audioAlertLowBatteryBeepCount: 5,
              audioAlertLowBatteryIntervalMinutes: 3,
              visualAlertEnabled: true,
              visualAlertBlinkCount: 3,
              visualAlertDurationSeconds: 30,
              visualAlertLowBatteryBlinkCount: 5,
              visualAlertLowBatteryDurationSeconds: 60,
            };

            if (currentStored) {
              try {
                baseConfig = { ...baseConfig, ...JSON.parse(currentStored) };
              } catch (e) {
                /* ignore */
              }
            }

            const newConfig: PlayerConfig = {
              ...baseConfig,
              ...remoteConfig,
              updatedAt: new Date().toISOString(),
            };

            localStorage.setItem(CONFIG_KEY, JSON.stringify(newConfig));

            // Apply volume — pulado se o host detém o controle (host-owned volume).
            if (
              !disableVolume &&
              newConfig.volumeLevel !== undefined &&
              !isHostVolumeControlled()
            ) {
              await volumeService.setVolume(newConfig.volumeLevel);
            }
            // Apply brightness (V046)
            applyBrightness(brightnessService, newConfig);
            // V058 — manter tela ativa (browser)
            applyKeepScreenAwake(platform, newConfig);

            // Cache background images from remote config
            const portraitUrl = resolveUrl(newConfig.backgroundPortraitUrl);
            const landscapeUrl = resolveUrl(newConfig.backgroundLandscapeUrl);
            if (portraitUrl || landscapeUrl) {
              console.log(
                '[ConfigManager] Caching background images from remote config:',
                { portraitUrl, landscapeUrl },
              );
            }
            cacheBackgroundImage(portraitUrl, 'efflux_bg_portrait');
            cacheBackgroundImage(landscapeUrl, 'efflux_bg_landscape');

            // Ack to server (Critical Fix for Sync Status)
            const playerId =
              authStore.playerId || localStorage.getItem('efflux_player_id');
            if (playerId) {
              console.log(
                '[ConfigManager] Sending Ack for fetched config (startup):',
                playerId,
              );
              syncStore.ackConfig(playerId).catch((err) => {
                console.error(
                  '[ConfigManager] Failed to send Ack (startup):',
                  err,
                );
              });
            }
          }
        } catch (error) {
          console.warn('[ConfigManager] Failed to fetch remote config:', error);
        }
      };

      if (deviceId) {
        // Tab A7: fetch remoto em 3s coincidia com relayout do WebView (~7s
        // pós-canPlay) e liberava o decoder OMX mid-playback. 30s dá tempo
        // pro primeiro vídeo estabilizar; SSE onConfigUpdate cobre urgências.
        setTimeout(doRemoteFetch, 30_000);
      } else {
        console.log(
          '[ConfigManager] Skipping remote config fetch - no device ID',
        );
      }
    };
    loadConfig();

    // Subscribe to SSE updates via SyncStore
    console.log('[ConfigManager] Subscribing to config updates', {
      isAuthenticated: authStore.isAuthenticated,
      deviceId: authStore.deviceId || localStorage.getItem('efflux_device_id'),
      playerId: authStore.playerId || localStorage.getItem('efflux_player_id'),
      syncStoreInitialized: syncStore.isInitialized,
    });

    let unsubscribe: (() => void) | undefined;
    let retryTimer: ReturnType<typeof setInterval> | null = null;

    const trySubscribe = () => {
      if (unsubscribe) return;
      const maybeUnsub = syncStore.onConfigUpdate(
        (config: {
          playerId?: string;
          volumeLevel?: number;
          resetVolumeTimeoutSeconds?: number;
          batteryWarningEnabled?: boolean;
          audioAlertEnabled?: boolean;
          audioAlertVolumeLevel?: number;
          audioAlertBeepCount?: number;
          audioAlertIntervalMinutes?: number;
          audioAlertLowBatteryBeepCount?: number;
          audioAlertLowBatteryIntervalMinutes?: number;
          visualAlertEnabled?: boolean;
          visualAlertBlinkCount?: number;
          visualAlertDurationSeconds?: number;
          visualAlertLowBatteryBlinkCount?: number;
          visualAlertLowBatteryDurationSeconds?: number;
          // SSE sends snake_case keys
          background_portrait_url?: string;
          background_landscape_url?: string;
          backgroundPortraitUrl?: string;
          backgroundLandscapeUrl?: string;
        }) => {
          console.log('[ConfigManager] Received config update:', config);
          if (config.volumeLevel === undefined) {
            console.warn(
              '[ConfigManager] volumeLevel missing in config update',
            );
          }

          console.log('[ConfigManager] Config update player context:', {
            authPlayerId: authStore.playerId,
            storedPlayerId: localStorage.getItem('efflux_player_id'),
            deviceId:
              authStore.deviceId || localStorage.getItem('efflux_device_id'),
            payloadPlayerId: config.playerId,
          });

          // Merge with existing config to avoid overwriting fields when payload is partial
          const storedConfig = localStorage.getItem(CONFIG_KEY);
          let existingConfig: PlayerConfig = {
            volumeLevel: 50,
            resetVolumeTimeoutSeconds: 0,
            batteryWarningEnabled: true,
            audioAlertEnabled: true,
            audioAlertVolumeLevel: 80,
            audioAlertBeepCount: 3,
            audioAlertIntervalMinutes: 5,
            audioAlertLowBatteryBeepCount: 5,
            audioAlertLowBatteryIntervalMinutes: 3,
            visualAlertEnabled: true,
            visualAlertBlinkCount: 3,
            visualAlertDurationSeconds: 30,
            visualAlertLowBatteryBlinkCount: 5,
            visualAlertLowBatteryDurationSeconds: 60,
          };
          if (storedConfig) {
            try {
              existingConfig = {
                ...existingConfig,
                ...JSON.parse(storedConfig),
              };
            } catch (e) {
              console.warn(
                '[ConfigManager] Failed to parse stored config, using defaults',
                e,
              );
            }
          }

          const newConfig: PlayerConfig = {
            ...existingConfig,
            ...config,
            updatedAt: new Date().toISOString(),
          };

          localStorage.setItem(CONFIG_KEY, JSON.stringify(newConfig));
          console.log('[ConfigManager] Saved config to localStorage:', {
            volumeLevel: newConfig.volumeLevel,
            resetVolumeTimeoutSeconds: newConfig.resetVolumeTimeoutSeconds,
          });

          // Resolve background URLs (SSE sends snake_case, also handle camelCase)
          const contentApiUrl = resolveApiBaseUrl();
          const resolveUrl = (url?: string): string | undefined => {
            if (!url) return undefined;
            if (url.startsWith('http://') || url.startsWith('https://'))
              return url;
            return `${contentApiUrl}${url.startsWith('/') ? '' : '/'}${url}`;
          };

          // Backend manda snake_case (background_X_url) e o config tipado expõe
          // camelCase (backgroundXUrl). Cast pra Record pra ler a snake_case
          // sem any — só essas 2 chaves específicas que ainda não estão no
          // type oficial do config.
          const cfgRaw = config as unknown as Record<string, unknown>;
          const portraitUrl = resolveUrl(
            (cfgRaw.background_portrait_url as string | undefined) ||
              config.backgroundPortraitUrl ||
              newConfig.backgroundPortraitUrl,
          );
          const landscapeUrl = resolveUrl(
            (cfgRaw.background_landscape_url as string | undefined) ||
              config.backgroundLandscapeUrl ||
              newConfig.backgroundLandscapeUrl,
          );

          // Cache background images for offline use
          cacheBackgroundImage(portraitUrl, 'efflux_bg_portrait');
          cacheBackgroundImage(landscapeUrl, 'efflux_bg_landscape');

          // Apply volume — pulado se o host detém o controle (host-owned volume).
          // A config ATUALIZADA já foi salva no localStorage acima, então quando
          // o host der releaseVolume o servidor volta com o valor mais recente.
          if (
            !disableVolume &&
            newConfig.volumeLevel !== undefined &&
            !isHostVolumeControlled()
          ) {
            volumeService.setVolume(newConfig.volumeLevel); // async but we don't block
          }
          // Apply brightness (V046) — synchronous, overlay DOM op
          applyBrightness(brightnessService, newConfig);
          // V058 — manter tela ativa (browser)
          applyKeepScreenAwake(platform, newConfig);

          // Ack to server
          // Only Ack if we have a valid playerId (even if verification is pending)
          // This fixes race condition where SSE arrives before HTTP verification completes
          const ackPlayerId =
            authStore.playerId ||
            config.playerId ||
            localStorage.getItem('efflux_player_id') ||
            undefined;
          if (ackPlayerId) {
            console.log('[ConfigManager] Sending Ack for player:', ackPlayerId);
            syncStore.ackConfig(ackPlayerId).catch((err) => {
              console.error('[ConfigManager] Failed to send Ack:', err);

              // SELF-HEALING: If server forbids us (403), our identity is invalid.
              // This happens if DB was wiped but client kept storage.
              // We must nuke local storage and re-register.
              if (err.response && err.response.status === 403) {
                console.warn(
                  '[ConfigManager] 🚨 403 Forbidden on ACK - Identity is invalid. Triggering Self-Healing...',
                );
                authStore.resetDeviceIdentity();
              }
            });
          } else {
            // Race de BOOT esperada: um config update chegou pelo SSE antes do
            // player resolver o playerId (SSE conecta antes da verificação HTTP
            // terminar). O config JÁ foi aplicado acima — só o ACK é pulado, e o
            // próximo config update (pós-auth) ackeia normal. Por isso é `debug`
            // (fica no LogRingBuffer pro troubleshooting remoto, mas fora do F12
            // por padrão) e não `warn` — não é anomalia. Comum só em sessão nova
            // /storage limpo; em device da frota (identidade persistida) é raro.
            console.debug(
              '[ConfigManager] Ack pulado: playerId ainda não resolvido (boot)',
              {
                isAuthenticated: authStore.isAuthenticated,
                hasPlayerId: !!authStore.playerId,
                payloadPlayerId: config.playerId,
                storedPlayerId: localStorage.getItem('efflux_player_id'),
              },
            );
          }

          // TODO: Implement Reset Logic (Timer)
          // For now we just apply the volume.
          // The reset logic requires monitoring volume changes and resetting after timeout.
          // This is complex as it requires polling volume or listening to volume changes.
          // VolumeService doesn't currently support listening to volume changes.
          // We'll leave the reset logic for a refinement step or polling implementation.
        },
      );
      if (maybeUnsub) {
        unsubscribe = maybeUnsub;
        console.log('[ConfigManager] Config update subscription active');
        if (retryTimer) {
          clearInterval(retryTimer);
          retryTimer = null;
        }
      } else {
        // Race benigna de boot — vai retentar sozinho. Não é warn.
        console.debug(
          '[ConfigManager] Config update subscription deferred - syncService not ready',
        );
      }
    };

    trySubscribe();
    if (!unsubscribe) {
      retryTimer = setInterval(trySubscribe, 500);
    }

    return () => {
      if (retryTimer) clearInterval(retryTimer);
      if (unsubscribe) unsubscribe();
    };
  }, [syncStore, authStore.isAuthenticated]); // Re-run if syncStore changes (unlikely)

  // Auto-reset Logic
  //
  // ⏱️ CADÊNCIA ADAPTATIVA (2026-09-19). Era `setInterval(..., 2000)` fixo, pra
  // sempre, em TODA plataforma — e cada ciclo chama `volumeService.getVolume()`,
  // que no Android é uma ida ao AudioManager via JNI. ~43k chamadas/dia por
  // device só pra descobrir que ninguém mexeu no volume.
  //
  // Agora o intervalo DERIVA do `resetVolumeTimeoutSeconds` que o operador
  // configurou: `clamp(timeout/2, 2s, 10s)`. A latência que o poll acrescenta
  // nunca passa de METADE do prazo que ele mesmo pediu, então a intenção dele é
  // preservada — e no caso comum (timeout longo, ou volume simplesmente igual
  // ao configurado, que é 99,9% do tempo) o custo cai até 5×.
  //
  //   timeout 0s  → poll 2s   (correção imediata: responsividade intacta)
  //   timeout 5s  → poll 2,5s
  //   timeout 60s → poll 10s
  //
  // Enquanto está FORA de sincronia volta pros 2s, que é quando responsividade
  // importa. ⚠️ Não trocar isto por um intervalo fixo grande: quebra quem
  // configurou timeout curto de propósito.
  useEffect(() => {
    if (disableVolume) {
      return;
    }
    const POLL_MIN_MS = 2_000;
    const POLL_MAX_MS = 10_000;
    const pollFor = (timeoutSeconds: number | undefined): number =>
      Math.min(
        POLL_MAX_MS,
        Math.max(POLL_MIN_MS, ((timeoutSeconds ?? 0) * 1000) / 2),
      );
    let timer: NodeJS.Timeout | undefined;
    let stopped = false;
    let nextDelayMs = POLL_MAX_MS;
    let outOfSyncSince: number | null = null;

    const startMonitoring = () => {
      const tick = async (): Promise<void> => {
        const storedConfig = localStorage.getItem(CONFIG_KEY);
        if (!storedConfig) return;

        try {
          const config: PlayerConfig = JSON.parse(storedConfig);
          nextDelayMs = pollFor(config.resetVolumeTimeoutSeconds);
          // Only run if volume is defined, auto-reset isn't disabled (-1), e o
          // host NÃO detém o controle (host-owned volume: enquanto o host manda,
          // o servidor não força o volume de volta). Fora do embedded o gate é
          // sempre false → monitor idêntico ao de antes.
          if (
            config.volumeLevel !== undefined &&
            config.resetVolumeTimeoutSeconds !== -1 &&
            !isHostVolumeControlled()
          ) {
            const currentVolume = await volumeService.getVolume();

            // Tolerance of 1? Sometimes rounding issues occur.
            // Let's assume exact match for now, or +/- 1.
            // VolumeService returns 0-100 integer.
            if (
              currentVolume !== -1 &&
              Math.abs(currentVolume - config.volumeLevel) > 1
            ) {
              const now = Date.now();
              const justWentOutOfSync = !outOfSyncSince;
              if (!outOfSyncSince) {
                outOfSyncSince = now;
              }
              const elapsedSeconds = (now - outOfSyncSince) / 1000;

              // Só na TRANSIÇÃO pra fora de sincronia. Antes logava a cada
              // ciclo enquanto durasse a dessincronia, o que enchia o
              // LogRingBuffer no mesmo padrão do VolumeService.
              if (justWentOutOfSync) {
                console.log(
                  `[ConfigManager] Volume out of sync (${currentVolume} vs ${config.volumeLevel}). Timeout: ${config.resetVolumeTimeoutSeconds}s`,
                );
              }

              if (
                config.resetVolumeTimeoutSeconds === 0 ||
                elapsedSeconds >= config.resetVolumeTimeoutSeconds
              ) {
                console.log(
                  '[ConfigManager] ⏰ Auto-resetting volume to default',
                );
                await volumeService.setVolume(config.volumeLevel);
                outOfSyncSince = null;
              }
            } else {
              // Volume is correct
              if (outOfSyncSince) {
                console.log('[ConfigManager] Volume back in sync');
                outOfSyncSince = null;
              }
            }
          } else {
            // Logic disabled or invalid config
            outOfSyncSince = null;
          }
        } catch (e) {
          console.error('[ConfigManager] Error in auto-reset loop:', e);
        }
      };

      // setTimeout auto-reagendado (em vez de setInterval): permite escolher o
      // próximo atraso conforme o estado, e nunca empilha execuções se um tick
      // demorar mais que o intervalo — o await só é seguido pelo agendamento.
      const loop = async (): Promise<void> => {
        if (stopped) return;
        await tick();
        if (stopped) return;
        timer = setTimeout(
          () => void loop(),
          outOfSyncSince ? POLL_MIN_MS : nextDelayMs,
        );
      };
      // Primeiro check DEPOIS do intervalo, não em t=0 — mesma semântica do
      // `setInterval` que isto substituiu (o boot já aplica o volume da config
      // por outro caminho; um check imediato aqui só duplicaria).
      timer = setTimeout(() => void loop(), POLL_MIN_MS);
    };

    startMonitoring();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return null; // This component handles logic only, no UI
};
